import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Cloud, Gauge, CheckCircle2, ChevronDown, ChevronUp, HelpCircle, Loader2, ShieldCheck, Timer, ClipboardList } from "lucide-react";

const DRIVE_GUIDE = [
  { n: 1, title: "Crea il Drive condiviso e la cartella", lines: [
    <>Vai su Google Drive.</>,
    <>A sinistra seleziona <strong>Drive condivisi</strong> e clicca su <strong>Nuovo</strong>.</>,
    <>Dentro il Drive condiviso crea la cartella in cui l'applicazione salverà i file.</>,
  ]},
  { n: 2, title: "Crea un progetto su Google Cloud Console", lines: [
    <>Apri il selettore di progetti in alto a sinistra e scegli <strong>Nuovo progetto</strong>.</>,
    <>Inserisci un nome e clicca su <strong>Crea</strong>.</>,
    <>Seleziona il progetto appena creato dal selettore in alto.</>,
  ]},
  { n: 3, title: "Abilita Google Drive API", lines: [
    <>Vai su <strong>API e servizi</strong>, poi <strong>Libreria</strong>.</>,
    <>Cerca «Google Drive API» e apri la scheda.</>,
    <>Clicca su <strong>Abilita</strong>.</>,
  ]},
  { n: 4, title: "Crea l'account di servizio e scarica il file JSON", lines: [
    <>Vai su <strong>API e servizi</strong>, poi <strong>Credenziali</strong>.</>,
    <>Clicca su <strong>Crea credenziali</strong> e scegli <strong>Account di servizio</strong>.</>,
    <>Assegna un nome, clicca su <strong>Crea e continua</strong> e poi su <strong>Fine</strong>.</>,
    <>Apri l'account di servizio appena creato e vai nella scheda <strong>Chiavi</strong>.</>,
    <>Clicca su <strong>Aggiungi chiave</strong>, poi <strong>Crea nuova chiave</strong> in formato JSON: si scarica un file.</>,
    <>Apri il file scaricato con un editor di testo, copia tutto il contenuto e incollalo nel campo <strong>Contenuto del file JSON delle credenziali</strong> qui sotto.</>,
  ]},
  { n: 5, title: "Recupera l'ID della cartella", lines: [
    <>Apri la cartella nel Drive condiviso.</>,
    <>Copia la parte dell'indirizzo dopo /folders/.</>,
    <>Incollala nel campo <strong>ID cartella radice Drive</strong> qui sotto.</>,
  ]},
  { n: 6, title: "Condividi la cartella con l'account di servizio", lines: [
    <>Trova l'email dell'account di servizio nel file JSON, alla voce client_email.</>,
    <>In Google Drive fai clic destro sulla cartella e scegli <strong>Condividi</strong>.</>,
    <>Incolla l'email, imposta il ruolo su <strong>Gestore</strong> o <strong>Collaboratore</strong> e togli la spunta dalle notifiche.</>,
    <>Clicca su <strong>Condividi</strong>.</>,
  ]},
];

export default function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [barrierEnabled, setBarrierEnabled] = useState(false);
  const [minScore, setMinScore] = useState("");
  const [durationA, setDurationA] = useState("30");
  const [durationB, setDurationB] = useState("15");
  const [privacyText, setPrivacyText] = useState("");
  const [rubric, setRubric] = useState([]);
  const [testMax, setTestMax] = useState("");
  const [driveJson, setDriveJson] = useState("");
  const [driveFolder, setDriveFolder] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verifyState, setVerifyState] = useState(null); // null | "error" | client_email

  const load = () => api.get("/admin/settings").then(({ data }) => {
    setSettings(data);
    setBarrierEnabled(data.min_score_a !== null && data.min_score_a !== undefined);
    setMinScore(data.min_score_a ?? "");
    setDurationA(String(data.duration_a_minutes ?? 30));
    setDurationB(String(data.duration_b_minutes ?? 15));
    setPrivacyText(data.privacy_text || "");
    setRubric(data.interview_rubric || []);
    setTestMax(data.test_max_score ?? "");
    setDriveFolder(data.drive?.root_folder_id || "");
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveBarrier = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { min_score_a: barrierEnabled && minScore !== "" ? Number(minScore) : null });
      toast.success("Sbarramento aggiornato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveDurations = async () => {
    const a = Number(durationA);
    const b = Number(durationB);
    if (!Number.isInteger(a) || a < 5 || a > 120) {
      toast.error("Durata Sezione A non valida: minimo 5, massimo 120 minuti");
      return;
    }
    if (!Number.isInteger(b) || b < 5 || b > 20) {
      toast.error("Durata Sezione B non valida: minimo 5, massimo 20 minuti");
      return;
    }
    setBusy(true);
    try {
      await api.put("/admin/settings", { duration_a_minutes: a, duration_b_minutes: b });
      toast.success("Durata dei test aggiornata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyDrive = async () => {
    setBusy(true);
    setVerifyState(null);
    try {
      const { data } = await api.post("/admin/settings/drive-verify", {
        drive_credentials_json: driveJson, drive_root_folder_id: driveFolder || null,
      });
      setVerifyState(data.client_email || "ok");
      toast.success("Connessione a Google Drive riuscita");
    } catch (e) {
      setVerifyState("error");
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveDrive = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { drive_credentials_json: driveJson, drive_root_folder_id: driveFolder || null });
      toast.success("Integrazione Google Drive salvata e attiva");
      setDriveJson("");
      setVerifyState(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveRubric = async () => {
    if (rubric.length === 0 || rubric.some((r) => !String(r.label || "").trim())) {
      toast.error("Ogni criterio deve avere un'etichetta");
      return;
    }
    if (rubric.some((r) => !(Number(r.max) > Number(r.min ?? 0)))) {
      toast.error("Per ogni criterio il punteggio massimo deve essere maggiore del minimo");
      return;
    }
    const tm = String(testMax).trim();
    if (tm !== "" && !(Number(tm) > 0)) {
      toast.error("Il punteggio massimo del test scritto deve essere un numero maggiore di zero");
      return;
    }
    setBusy(true);
    try {
      await api.put("/admin/settings", { interview_rubric: rubric, test_max_score: tm === "" ? 0 : Number(tm) });
      toast.success("Configurazione valutazione aggiornata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const savePrivacy = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { privacy_text: privacyText });
      toast.success("Privacy Policy aggiornata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <p className="text-sm text-slate-500">Caricamento impostazioni...</p>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="institutional-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Gauge className="h-5 w-5 text-[#003366]" />
            <h3 className="font-display font-semibold text-slate-900">Sbarramento Sezione A</h3>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Se attivo, i candidati che non raggiungono il punteggio minimo nella sezione A non accedono alla
            sezione B: il test si chiude e il candidato vede solo la conferma di completamento (senza punteggio).
          </p>
          <div className="mt-5 flex items-center gap-3">
            <Switch data-testid="settings-barrier-toggle" checked={barrierEnabled} onCheckedChange={setBarrierEnabled} />
            <Label>Abilita sbarramento</Label>
          </div>
          {barrierEnabled && (
            <div className="mt-4">
              <Label>Punteggio minimo (somma punti risposte corrette)</Label>
              <Input data-testid="settings-min-score" type="number" min={0} step="0.5" className="mt-2 w-40"
                value={minScore} onChange={(e) => setMinScore(e.target.value)} />
            </div>
          )}
          <Button data-testid="settings-barrier-save" className="mt-5 bg-[#003366] hover:bg-[#002244]" disabled={busy} onClick={saveBarrier}>
            Salva sbarramento
          </Button>
        </CardContent>
      </Card>

      <Card className="institutional-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Timer className="h-5 w-5 text-[#003366]" />
            <h3 className="font-display font-semibold text-slate-900">Durata dei test</h3>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Tempo massimo a disposizione dei candidati per ciascuna sezione. Allo scadere del tempo la
            sezione viene consegnata automaticamente. Si applica ai test avviati dopo il salvataggio.
          </p>
          <div className="mt-5 space-y-4">
            <div>
              <Label>Sezione A — Test tecnico (minuti)</Label>
              <Input data-testid="settings-duration-a" type="number" min={5} max={120} step={1} className="mt-2 w-40"
                value={durationA} onChange={(e) => setDurationA(e.target.value)} />
              <p className="text-xs text-slate-500 mt-1">Min 5, max 120. Predefinito: 30 minuti.</p>
            </div>
            <div>
              <Label>Sezione B — Questionario orientativo (minuti)</Label>
              <Input data-testid="settings-duration-b" type="number" min={5} max={20} step={1} className="mt-2 w-40"
                value={durationB} onChange={(e) => setDurationB(e.target.value)} />
              <p className="text-xs text-slate-500 mt-1">Min 5, max 20. Predefinito: 15 minuti (valore raccomandato).</p>
            </div>
          </div>
          <Button data-testid="settings-durations-save" className="mt-5 bg-[#003366] hover:bg-[#002244]" disabled={busy} onClick={saveDurations}>
            Salva durata test
          </Button>
        </CardContent>
      </Card>
      </div>

      <Card className="institutional-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <Cloud className="h-5 w-5 text-[#003366]" />
              <h3 className="font-display font-semibold text-slate-900">Integrazione Google Drive</h3>
            </div>
            <Badge data-testid="drive-status-badge" className={settings.drive?.configured ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}>
              {settings.drive?.configured ? "Attiva" : "Non configurata"}
            </Badge>
          </div>

          {settings.drive?.configured && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 mb-4" data-testid="drive-configured-info">
              I documenti dei candidati vengono salvati sul Drive dell'istituto ({settings.drive.client_email}), in sottocartelle per candidato.
            </p>
          )}
          {!settings.drive?.configured && (
            <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
              Fino al completamento della configurazione, i documenti vengono salvati sull'object storage interno della piattaforma.
            </p>
          )}

          {settings.drive?.last_error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-4" data-testid="drive-last-error">
              Ultimo upload su Drive fallito ({settings.drive.last_error_at ? new Date(settings.drive.last_error_at).toLocaleString("it-IT") : ""}):
              {" "}{settings.drive.last_error}. I documenti interessati sono stati salvati sullo storage interno.
            </p>
          )}

          <div className="rounded-lg border border-slate-200" data-testid="drive-guide">
            <button type="button" data-testid="drive-guide-toggle" onClick={() => setGuideOpen((o) => !o)}
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors hover:bg-slate-50">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <HelpCircle className="h-4 w-4 text-[#003366]" /> Come configurare Google Drive
              </span>
              {guideOpen ? <ChevronUp className="h-4 w-4 shrink-0 text-slate-500" /> : <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />}
            </button>
            {guideOpen && (
              <div className="border-t border-slate-200 px-4 py-4">
                <p className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="drive-guide-workspace-warning">
                  <strong>Prima di iniziare:</strong> serve un account <strong>Google Workspace</strong>, perché i Drive condivisi non sono disponibili con i normali account Gmail gratuiti. Gli account didattici (Google Workspace for Education) sono abilitati.
                </p>
                <ol className="space-y-4">
                  {DRIVE_GUIDE.map((s) => (
                    <li key={s.n} className="flex gap-3">
                      <div className="h-7 w-7 shrink-0 rounded-full bg-[#E0E7FF] text-[#003366] flex items-center justify-center text-xs font-bold">{s.n}</div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                        <div className="mt-0.5 space-y-1">
                          {s.lines.map((l, i) => (
                            <p key={i} className="text-xs text-slate-600 leading-relaxed">{l}</p>
                          ))}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          <div className="mt-5 space-y-4 border-t border-slate-100 pt-5">
            <div>
              <Label>Contenuto del file JSON delle credenziali</Label>
              <Textarea data-testid="drive-credentials-json" rows={4} className="mt-2 font-mono2 text-xs"
                placeholder='{"type": "service_account", "project_id": "...", "private_key": "...", "client_email": "..."}'
                value={driveJson} onChange={(e) => { setDriveJson(e.target.value); setVerifyState(null); }} />
            </div>
            <div>
              <Label>ID cartella radice Drive</Label>
              <Input data-testid="drive-root-folder" className="mt-2 max-w-md" placeholder="es. 1AbCdefGhI..."
                value={driveFolder} onChange={(e) => { setDriveFolder(e.target.value); setVerifyState(null); }} />
            </div>
            {verifyState && verifyState !== "error" && (
              <p className="text-sm text-green-700 flex items-center gap-2" data-testid="drive-verify-ok">
                <CheckCircle2 className="h-4 w-4" /> Connessione verificata: {verifyState}
              </p>
            )}
            <div className="flex gap-3">
              <Button data-testid="drive-verify-button" variant="outline" disabled={busy || !driveJson.trim()} onClick={verifyDrive}>
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Verifica connessione
              </Button>
              <Button data-testid="drive-save-button" className="bg-[#003366] hover:bg-[#002244]"
                disabled={busy || !driveJson.trim() || !verifyState || verifyState === "error"} onClick={saveDrive}
                title={!verifyState || verifyState === "error" ? "Prima verifica la connessione" : "Salva la configurazione verificata"}>
                Salva configurazione
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="institutional-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="h-5 w-5 text-[#003366]" />
            <h3 className="font-display font-semibold text-slate-900">Configurazione valutazione</h3>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Definisci come la commissione valuta il colloquio: un'etichetta e un punteggio massimo per ciascuno dei
            5 item, a scelta libera dell'istituto. Il punteggio complessivo del candidato è la somma del test scritto
            e dei punteggi di colloquio, ed è completo solo dopo l'inserimento di tutti i criteri. Le valutazioni già
            inserite restano salvate anche se modifichi questa configurazione.
          </p>
          <div className="mt-5" data-testid="rubric-editor">
            <div className="border border-slate-100 rounded-lg p-3 mb-3 max-w-md">
              <Label className="text-xs">Punteggio massimo del test scritto</Label>
              <Input data-testid="eval-test-max-input" type="number" min="0" step="any" className="mt-1"
                value={testMax} onChange={(e) => setTestMax(e.target.value)}
                placeholder={`Vuoto = punteggio della banca domande (attuale: ${settings?.current_test_max ?? "—"})`} />
              <p className="text-[11px] text-slate-500 mt-1">
                Se impostato, il punteggio del test scritto viene riproporzionato su questo massimo.
              </p>
            </div>
            <div className="space-y-3">
              {rubric.map((r, i) => (
                <div key={i} className="flex flex-wrap items-end gap-3 border border-slate-100 rounded-lg p-3">
                  <div className="flex-1 min-w-[180px]">
                    <Label className="text-xs">Etichetta item {i + 1} del colloquio</Label>
                    <Input data-testid={`rubric-label-${i}`} className="mt-1" maxLength={80} value={r.label}
                      onChange={(e) => setRubric(rubric.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} />
                  </div>
                  <div className="w-28">
                    <Label className="text-xs">Punteggio max</Label>
                    <Input data-testid={`rubric-max-${i}`} type="number" min="0" step="any" className="mt-1" value={r.max}
                      onChange={(e) => setRubric(rubric.map((x, xi) => (xi === i ? { ...x, max: e.target.value === "" ? "" : Number(e.target.value) } : x)))} />
                  </div>
                  <Button data-testid={`rubric-remove-${i}`} variant="outline" size="sm" disabled={rubric.length <= 1}
                    onClick={() => setRubric(rubric.filter((_, xi) => xi !== i))}>Rimuovi</Button>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <Button data-testid="rubric-add" variant="outline" disabled={rubric.length >= 5}
              onClick={() => setRubric([...rubric, { id: `item_${rubric.length + 1}`, label: "", min: 0, max: 10 }])}>
              Aggiungi item
            </Button>
            <Button data-testid="rubric-save" className="bg-[#003366] hover:bg-[#002244]" disabled={busy} onClick={saveRubric}>
              Salva configurazione
            </Button>
          </div>
          <div className="mt-5 rounded-xl border-2 border-[#003366] bg-blue-50/60 px-5 py-4 flex items-center justify-between"
            data-testid="eval-total-max">
            <span className="text-sm font-semibold text-slate-900">Punteggio totale massimo</span>
            <span className="font-mono2 text-2xl font-bold text-[#003366]">
              {(String(testMax).trim() !== "" && Number(testMax) > 0 ? Number(testMax) : (settings?.current_test_max ?? 0))
                + rubric.reduce((s, r) => s + (Number(r.max) || 0), 0)}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Test scritto più somma dei punteggi massimi dei {rubric.length} item del colloquio; aggiornato mentre modifichi i valori.
          </p>
        </CardContent>
      </Card>

      <Card className="institutional-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5 text-[#003366]" />
            <h3 className="font-display font-semibold text-slate-900">Privacy Policy dell'istituto</h3>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Testo mostrato ai candidati nella pagina <span className="font-mono2 text-xs">/candidati/{settings.slug}/privacy</span> e
            da accettare obbligatoriamente all'inizio della candidatura.
          </p>
          <Textarea data-testid="settings-privacy-text" rows={10} className="mt-4 text-sm" value={privacyText}
            onChange={(e) => setPrivacyText(e.target.value)} />
          <Button data-testid="settings-privacy-save" className="mt-4 bg-[#003366] hover:bg-[#002244]"
            disabled={busy || !privacyText.trim()} onClick={savePrivacy}>
            Salva Privacy Policy
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
